/* eslint-disable */
/* tslint:disable */

import {
  ApiUpdateBulk,
  ApiLocationSummary,
  LocationId,
  IpAddress,
  MacAddress,
  ApiTimeZone,
  Guid,
  ApiDeviceCredentials,
  ApiAdapterInfo,
} from './common';
import { DeviceAddress, DeviceStatus } from './devices';
import { PaginatedResponse } from './pagination';
import { FloorCoords } from './locations';
import { ApiDewarpConfig } from './composites';
import { DeviceStatusFilterParams } from './devices';

export type Oak = string;

export type Serial = string;

export type ApiPaginatedCameraResponse = PaginatedResponse & {
  results?: ApiCameraWithIncludes[];
};

export type ApiCameraWithIncludes = ApiCameraMain & ApiCameraIncludes & object;
export type SwapCameraParams = {
  cameraId: string;
  guid: Guid;
};

export enum CameraZoom {
  ContinuousZoom = 'continuousZoom',
}

export type CameraTuningParams = {
  type: CameraZoom.ContinuousZoom;
  /** The direction to move in. */
  direction: 'in' | 'out';
  /** The duration in seconds to move in the given direction.*/
  durationSec: number;
};

export type GetCamerasParams = {
  /**
   * List of Location IDs to filter on that is comma separated.
   * @example ["2428fda3-389f-48ce-b062-85875dd6b2e2"]
   */
  locationId__in?: string[];
  /** List of Bridge IDs to filter on that is comma separated. */
  bridgeId__in?: string;
  /** Filter to get cameras with given multiCameraId. multiCameraId=null returns cameras that are not multi camera cameras. */
  multiCameraId?: string;
  /** Filter to get cameras with multiCameraId that is not equal to given value. */
  multiCameraId__ne?: string;
  /** List of multi camera IDs to filter on that is comma seperated. */
  multiCameraId__in?: string;
  /**
   * Only return cameras that have all tags in the list, separated by commas.
   * @example ["string"]
   */
  tags__contains?: string[];
  /**
   * Only return cameras that have one or more of the tags in the list, separated by commas.
   * @example ["string"]
   */
  tags__any?: string[];
  /**
   * Only return cameras that have enabled all packages in the list, separated by commas.
   * @example ["een.vsp"]
   */
  packages__contains?: string[];
  /** Filter to get cameras that are part of the given layout. */
  layoutId?: string;
  /** Filter to get the cameras whose the name contains the provided substring. The lookup is exact and case insensitive */
  name__contains?: string;
  /** Filter to get the cameras whose name is on the provided list. The lookup is exact and case insensitive. */
  name__in?: string[];
  /** Filter to get the cameras with the specified name. The lookup is exact and case insensitive. */
  name?: string;
  /** Filter to get the cameras whose id is on the provided list. The lookup is exact and case insensitive. */
  id__in?: string[] | string;
  /** Filter to exlude the cameras whose ids are in the provided list. The lookup is exact and case insensitive. */
  id__notIn?: string[] | string;
  /** Filter to get the cameras whose the id contains the provided substring. The lookup is exact and case insensitive */
  id__contains?: string;
  /** If set to `true`, only cameras that are shared with the current account by another account will be returned. If set to `false`, the cameras that are shared with the current account will be filtered out. */
  'shareDetails.shared'?: boolean;
  /** Filter to get the cameras that are shared from the provided account. */
  'shareDetails.accountId'?: string;
  /** If set to `true`, only cameras that are shared with the current account using the "First responder" feature will be returned. If set to `false`, the cameras that are shared with the current account using the "First responder" feature will be filtered out. */
  'shareDetails.firstResponder'?: boolean;
  /** If set to `true`, only cameras that connect directly to the VMS cloud, and not through a bridge will be returned. If set to `false`, the cameras that connect directly to the VMS cloud will be filtered out. */
  'deviceInfo.directToCloud'?: boolean;
  /** Filter to get cameras that are a part of the given speaker esn. */
  speakerId__in?: string[];
  /**
   * Text search that is applied to multiple fields. The fields being searched are defined by the backend and can be changed without warning. Example fields being searched are metadata fields of the camera itself such as `id`, `name`, `notes`, `timezone`, `multiCameraId`, `speakerId` and `tags`; the important ids such as `accountId`, `bridgeId`, and, `locationId`, and metadata of important linked resources such as the location, bridge, share details, device position, device info and the viewports configured for the camera.
   * @example "entrance"
   */
  q?: string;
  /**
   * Sets the current minimum similarity threshold that is used with the `q` parameter. The threshold must be between 0 and 1 (float, default is 0.5).
   * @format float
   * @min 0
   * @max 1
   * @default 0.5
   * @example 0.5
   */
  qRelevance__gte?: number;
  /** The include parameter is used to specify which fields are included in the response. 
  *  The include parameter accepts a comma-separated list of field names. For example, 
  *  E.g.: include=schema,proposedValues. Available values are:
  * | "status"
    | "locationSummary"
    | "deviceAddress"
    | "timeZone"
    | "notes"
    | "tags"
    | "devicePosition"
    | "networkInfo"
    | "deviceInfo"
    | "firmware"
    | "visibleByBridges"
    | "capabilities"
    | "effectivePermissions"
    | "shareDetails"
    | "analog"
    | "qRelevance"
    | "packages"
    | "dewarpConfig"
  */

  /**
   * Only return cameras that have enabled all analytics in the comma-separated list.
   * @example ["een.tamperDetectionEvent.v1"]
   */
  enabledAnalytics__contains?: string[];

  include?: string[] | string;
  /**
   * Comma separated list of of fields that should be sorted.
   *  * `sort=` - not providing any value will result in error 400
   *  * `sort=+name,+name` - same values will result in error 400
   *  * `sort=-name,+name` - mutially exclusive values will return error 400
   *  * maxItem=3 - Only Three values will be accpeted, more will return error 400
   *  * qRelevance is optional ordering parameter which is available if q filter is used, if q filter is not passed qRelevance as ordering parameter will return error 400
   * @default ["+name"]
   * @example ["+name","-locationSummary.name"]
   * sort by options 
    | "-name"
    | "-locationSummary.name"
    | "-bridgeSummary.name"
    | "-qRelevance"
    | "+name"
    | "+locationSummary.name"
    | "+bridgeSummary.name"
    | "+qRelevance"
   */
  sort?: string | string[];
  pageSize?: number;
  pageToken?: string;
} & DeviceStatusFilterParams;

export type DeleteCameraId = string;

export type ApiUpdateBulkCamera = ApiUpdateBulk & {
  updateFields: ApiUpdateBulk['updateFields'] & { speakerId?: SpeakerId };
};

export interface ApiCameraMain {
  id: string;
  accountId: string;
  name: string;

  /**
   * The bridge a camera is connected to. Keep in mind that some cameras are connected directly to the cloud, which is when this field will not be reported.
   *
   */
  bridgeId: BridgeId;

  /** ID Of the location. */
  locationId?: LocationId;
}

export interface ApiCameraIncludes {
  status?: {
    connectionStatus: DeviceStatus;
  };

  rtsp?: RtspConnectionSettings;

  /** Summary of the location object, see /locations to get more information. */
  locationSummary?: ApiLocationSummary | null;
  deviceAddress?: DeviceAddress;

  /**
   * Optional unique identifier of the speaker device associated with this camera. This should be a speaker that is audible to anyone visible in the camera view, allowing an operator to talk to visible people.
   *
   */
  speakerId?: SpeakerId;
  timeZone?: ApiTimeZone;

  /** @example Previously used for laser eye surgery. Records pizzas as squares */
  notes?: string;
  tags?: DeviceTags;
  devicePosition?: ApiDevicePositionCamera;
  networkInfo?: ApiNetworkInfoCamera;
  deviceInfo?: ApiDeviceInfoCamera;
  firmware?: ApiFirmwareCamera;
  adminCredentials?: ApiDeviceCredentials;
  visibleByBridges?: string[];
  capabilities?: ApiCameraCapabilities;
  positionsRelatedToFloor?: FloorCoords;
  effectivePermissions?: EffectiveCameraPermissions;
  shareDetails?: ShareDetails;
  analog?: Analog;
  dewarpConfig?: ApiDewarpConfig;
  /** Filter to get cameras with given multiCameraId. multiCameraId=null returns cameras that are not multi camera cameras. */
  multiCameraId?: string;
  publicSafetySharing?: {
    enabled: boolean;
  };
}

export interface Analog {
  /**
   * The identifier of the analog input.
   * @example 1
   */
  input?: number | null;
}

export interface ShareDetails {
  /** If true, this camera is owned by another account and shared with the current account. This includes cameras shared using the camera sharing feature, and cameras shared using the first responder feature. */
  shared?: boolean;
  /** If true, this camera is shared as a first responder camera. */
  firstResponder?: boolean;
  /**
   * If the camera is shared, this is the ID of the account that shared this camera with the current account. Null if the camera is not shared.
   * @example "00009983"
   */
  accountId?: string | null;
  /**
   * If the camera is shared, this is the name of the account that shared this camera with the current account. Null if the camera is not shared.
   * @example "EEN Demo."
   */
  accountName?: string | null;
}

export interface EffectiveCameraPermissions {
  read?: boolean;
  addToLayout?: boolean;
  /** View preview images from cameras */
  viewPreviewVideo?: boolean;
  /** View full resolution video live */
  viewLiveVideo?: boolean;
  /** View historic video */
  viewHistoricVideo?: boolean;
  /** Turn Camera on/off */
  turnOnOff?: boolean;
  /** Control Pan, Tilt, Zoom, & recall stations while viewing a preview or live video of PTZ cameras */
  controlPTZ?: boolean;
  /** Set PTZ mode and create, delete, edit stations */
  editPTZStations?: boolean;
  /** Download preview and full resolution video */
  downloadVideo?: boolean;
  delete?: boolean;
  /** Editing all Camera Settings */
  editBillingSettings?: boolean;
  /** Edit Camera Settings except for those that affect billing, like Cloud Retention and Full Video Resolution */
  editNoBillingSettings?: boolean;
  /**
   * Set Motion object size and sensitivity, add/delete regions, add/delete alerts for regions. Edit and view existing Analytics. Set object size and sensitivity, add/delete alerts
   *
   */
  editMotionAreas?: boolean;
  /** Allows the users to initiate Talk Down to the camera field(s) of view */
  talkDown?: boolean;
}

export interface ApiBaseCameraCreate {
  /**
   * The name to assign to the camera. It is used to help identifying the camera in the account.
   *
   */
  name?: CameraName;
  tags?: DeviceTags;

  /** ID Of the location. */
  locationId?: LocationId;
}

/**
 * Base schema for cameras that are attached to a bridge, vs cameras that are connected directly to the cloud.
 */
export type ApiBridgedCameraCreate = ApiBaseCameraCreate & {
  bridgeId?: BridgeId;
  credentials?: ApiDeviceCredentials;
};

/**
 * Schema used to describe a new camera to be created that should be attached to a bridge.
 */
export type ApiBridgeCameraCreate = ApiBridgedCameraCreate & { guid?: Guid };

/**
 * Schema used to describe a new virtual camera to be created that should be created based on an RTSP stream.
 */
export type ApiRtspCameraCreate = ApiBridgedCameraCreate & {
  rtspSettings?: RtspConnectionSettings;
};

/**
 * Schema used to describe a new camera to be created that directly connects to the cloud.
 */
export type ApiCameraDirectCreate = ApiBaseCameraCreate & {
  macAddress?: MacAddress;
};

/** Schema used to describe a new Axis camera to be created that directly connects to the cloud. */
export type ApiAxisCameraDirectCreate = ApiCameraDirectCreate & {
  /** Owner Authentication Key */
  oak: Oak;
  serial: Serial;
};

/**
 * Base schema to describe all new cameras to be created.
 */
export type ApiAddCamera = (
  | ApiBridgeCameraCreate
  | ApiRtspCameraCreate
  | ApiCameraDirectCreate
  | ApiAxisCameraDirectCreate
) & {
  registrationStrategy: ApiRegistrationStrategy;
};

/**
 * Cameras can be added in multiple ways, and this field indicates how this specific camera should be added. Possible methods:
 * `bridge`: Associate a camera found with `GET /api/v3.0/availableDevices` with one of the bridges that found it.
 * `rtspUrl`: Define a camera by giving a RTSP URL to retrieve its live stream and the bridge that should connect to it.
 * `cameraDirect`: Register a camera by its MAC address regardless of its physical location and without needing a bridge.
 */
export enum ApiRegistrationStrategy {
  Bridge = 'bridge',
  RtspUrl = 'rtspUrl',
  CameraDirect = 'cameraDirect',
  Channel = 'channel',
  AxisCameraDirect = 'axisCameraDirect',
}

/**
 * Describes all settings currently supported to connect to an RTSP stream.
 */
export interface RtspConnectionSettings {
  ipAddress?: string;

  /**
   * The RTSP URL to connect to the main video (eg. h.264) stream will be created using the IP address and this URL.
   *
   * @example live.sdp
   */
  videoResourceUrl?: string;

  /**
   * The RTSP URL to connect to the preview video (eg. MJPEG) stream will be created using the IP address and this URL.
   *
   * @example live2.sdp
   */
  previewResourceUrl?: string;
}

/**
 * The name to assign to the camera. It is used to help identifying the camera in the account.
 */
export type CameraName = string;

/**
 * The bridge a camera is connected to. Keep in mind that some cameras are connected directly to the cloud, which is when this field will not be reported.
 * @pattern ^(?:0[xX])?[0-9a-fA-F]{8}$
 * @example 1000f60d
 */
export type BridgeId = string;

/**
 * Optional unique identifier of the speaker device associated with this camera. This should be a speaker that is audible to anyone visible in the camera view, allowing an operator to talk to visible people.
 * @example 10039dab
 */
export type SpeakerId = string | null;

export type DeviceTags = string[];

export interface ApiDevicePositionCamera {
  /**
   * The latitude of the device. (WGS 84)
   * @format double
   * @min -90
   * @max 90
   * @example 52.3074013
   */
  latitude?: number | null;

  /**
   * The longitude of the device. (WGS 84).
   * @format double
   * @min -180
   * @max 180
   * @example 4.9459903
   */
  longitude?: number | null;

  /**
   * @format double
   * @min 0
   * @max 360
   * @example 7.419435
   */
  azimuth?: number | null;

  /**
   * @format double
   * @min 0
   * @example 37.622658
   */
  rangeInFeet?: number | null;

  /**
   * @format double
   * @min 0
   * @example 50.4
   */
  rangeInMeters?: number | null;

  /**
   * @min 0
   * @example 50
   */
  floor?: number | null;

  /**
   * Format: double
   * @description Field of view is the range of the observable world visible at any given time through camera viewfinder.
   * @example 100
   */
  fieldOfView?: number | null;
}

export interface ApiNetworkInfoCamera {
  globalIP?: IpAddress | null;
  adapters?: ApiAdapterInfo[];
}

export interface ApiDeviceInfoCamera {
  /**
   * This field allows UIs to roughly differentiate between camera types when displaying a list of cameras. * Important: this should not be used in any way to decide on what functionality is available for a camera, that logic should be based on the capabilities and possibly the settings endpoints. * Important: more enum values might be added in the future, clients should handle new values by having a fallback icon, eg a generic "security camera" icon. * Important: The value for a specific model might change without notice.
   *
   */
  icon?: 'securityCamera' | 'bodycam' | 'doorbell';

  /** The Manufactorer or brand of the camera. */
  make?: string;

  /** The model of the camera. */
  model?: string;
  guid?: Guid;
}

export interface ApiFirmwareCamera {
  /** @example v20211019_542_3022 */
  currentVersion?: string;

  /**
   * Indicates what the current status is of the device's firmware: * `up-to-date`: The device's firmware is fully up-to-date. * `upgradable`: The device's firmware can be upgraded. * `unknown`: It is currently unknown if the device's firmware can and should be upgraded.
   *
   */
  status?: 'up-to-date' | 'upgradable' | 'unknown';

  /**
   * Describes, if known, what firmware version this device shuold be upgraded to.
   * @example v20211025_544_6753
   */
  targetVersion?: string;
}

export interface ApiCameraUpdate {
  /**
   * Name of the camera
   */
  name?: string;

  /**
   * Notes
   * @example Camera used for demo purposes
   */
  notes?: string;

  /**
   * Optional unique identifier of the speaker device associated with this camera. This should be a speaker that is audible to anyone visible in the camera view, allowing an operator to talk to visible people.
   *
   */
  speakerId?: SpeakerId;

  /** ID Of the location. */
  locationId?: LocationId;
}

export interface ApiCameraCapabilities {
  ptz: ApiCameraPTZCapabilities;
  audio: ApiCameraAudioCapabilities;
  varifocal: boolean;
  io: ApiCameraIOCapabilities;
}

export interface ApiCameraIOCapabilities {
  /**
   * Idicates that the camera support input / output ports
   * @default false
   */
  capable?: boolean;
}

export interface ApiCameraPTZCapabilities {
  /**
   * Indicates that the camera has some PTZ capability. Check the other capabilities for what exact capability it has.
   *
   */
  capable: boolean;

  /**
   * Indicates that the camera can be panned and tilted using the API.
   *
   */
  panTilt?: boolean;

  /**
   * Indicates that the camera can be zoomed in and out using the API.
   *
   */
  zoom?: boolean;

  /**
   * Indicates that the camera supports the "position" move, see the PTZ API for details.
   *
   */
  positionMove?: boolean;

  /**
   * Indicates that the camera supports the "direction" move, see the PTZ API for details.
   *
   */
  directionMove?: boolean;

  /**
   * Indicates that the camera supports the "centerOn" move, see the PTZ API for details.
   *
   */
  centerOnMove?: boolean;

  /**
   * Indicates that the camera supports the "fisheye", see the PTZ API for details.
   *
   */
  fisheye?: boolean;
}

export interface ApiCameraAudioCapabilities {
  /** Indicates that audio is recorded by the camera. The source of audio can be microphone or other camera. */
  recordingEnabled: boolean;
  /** Indicates that audio can be recorded by the camera, or an associated microphone. */
  microphone?: boolean;

  /** Indicates that audio can be broadcast by the camera, or an associated speaker. */
  speaker?: boolean;

  /**
   * Indicates that audio can be used to communicate in both directions, but only one direction at a time. Also known as "push to talk", this behaves the same as most "walkie-talkie" systems: only one side can talk at a time.
   *
   */
  halfDuplex?: boolean;

  /**
   * Indicates that audio can be used to communicate in both directions simultaneously. This behaves similarly to most telephone systems where both sides can talk at the same time.
   *
   */
  fullDuplex?: boolean;
}

export type CameraTunnelResponse = {
  /**
   * Unencrypted tunnel URL, available at all time.
   * @example "http://example.com"
   */
  httpUrl: string;
  /**
   * Encrypted tunnel URL when supported by the device.
   * @example "https://example.com"
   */
  httpsUrl?: string;
};

export interface getCamerasFieldParams {
  /** Defines the fields for which the system should return a list of possible values. */
  include?: 'shareDetails.account';
  /** If set to `true`, only cameras that are shared with the current account using the "First responder" feature will be returned. If set to `false`, the cameras that are shared with the current account using the "First responder" feature will be filtered out. */
  'shareDetails.firstResponder'?: boolean;
}

export interface CameraFieldValues {
  /** List of observed values of the accountName and accountId subfields in the shareDetails field. */
  'shareDetails.account'?: {
    /**
     * The ID of the account that shared this camera with the current account.
     * @example "00009983"
     */
    id?: string;
    /**
     * The account that shared this camera with the current account.
     * @example "EEN Demo."
     */
    name?: string;
  }[];
}

export interface CameraTunnelParams {
  bridge_id: string;
  guid: string;
}
